/**
 * Student prep / notification helpers for WebGazer client monitoring.
 * Pure functions — unit-tested; keep ETH-XGaze wording out of the live path.
 */

export type PrepCameraState = {
  active: boolean;
  error?: string;
};

export type PrepReadiness = {
  isReady: boolean;
  readinessLabel: string;
  statusLabel: 'Nominal' | 'Waiting' | 'Camera issue';
  statusKind: 'nominal' | 'connecting' | 'critical';
  step2Title: string;
  step2Hint: string;
  startButtonLabel: string;
};

/** Camera-only gate: WebGazer look-and-click calibration happens on the exam screen. */
export function getStudentPrepReadiness(camera: PrepCameraState): PrepReadiness {
  if (camera.error && !camera.active) {
    return {
      isReady: false,
      readinessLabel: 'Camera unavailable',
      statusLabel: 'Camera issue',
      statusKind: 'critical',
      step2Title: 'Gaze calibration (next)',
      step2Hint:
        'Fix camera access first. On the exam screen you will click each WebGazer circle 5 times, then need ≥75% accuracy to start.',
      startButtonLabel: 'Camera required',
    };
  }

  if (!camera.active) {
    return {
      isReady: false,
      readinessLabel: 'Waiting for camera',
      statusLabel: 'Waiting',
      statusKind: 'connecting',
      step2Title: 'Gaze calibration (next)',
      step2Hint:
        'Allow the camera. Gaze personalization uses WebGazer look-and-click (5 clicks per circle, ≥75% accuracy) on the exam screen — not a server baseline.',
      startButtonLabel: 'Waiting for camera…',
    };
  }

  return {
    isReady: true,
    readinessLabel: 'Camera ready',
    statusLabel: 'Nominal',
    statusKind: 'nominal',
    step2Title: 'Gaze calibration (next)',
    step2Hint:
      'When you start, click each circle 5 times while watching the cursor (WebGazer). You need ≥75% measured accuracy before the exam unlocks.',
    startButtonLabel: 'Start exam',
  };
}

/** Success toast when local camera starts — must not claim ETH-XGaze. */
export function cameraActiveSuccessMessage(): string {
  return 'Camera ready. Gaze calibration (WebGazer look-and-click) runs when you start the exam.';
}

/**
 * WS/API errors that are expected in webgazer mode and must not block the UI.
 */
export function shouldSurfaceMonitoringError(message: string | null | undefined): boolean {
  if (!message || !message.trim()) return false;
  const lower = message.toLowerCase();
  if (lower.includes('monitoring_mode=webgazer')) return false;
  if (lower.includes('eth-xgaze frame processing is disabled')) return false;
  if (lower.includes('gaze runs in the browser')) return false;
  return true;
}
